import { SidebarCompactPopover } from '@/components/SidebarCompactPopover';
import { Select } from '@/components/ui/Select';
import { useOrg } from '@/contexts/OrgContext';
import { Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

type OrgSwitcherProps = {
  variant?: 'default' | 'sidebar' | 'sidebarCompact';
};

export function OrgSwitcher({ variant = 'default' }: OrgSwitcherProps) {
  const { t } = useTranslation();
  const { org, memberships, switchOrg } = useOrg();

  if (memberships.length <= 1) {
    if (!org) return null;
    /* Sidebar header already shows brandName — no second org label */
    if (variant === 'sidebar') return null;
    return <p className="text-sm font-semibold text-[var(--fv-text)]">{org.name}</p>;
  }

  const options = memberships.map((m) => ({ value: m.orgId, label: m.orgName }));

  if (variant === 'sidebarCompact') {
    return (
      <SidebarCompactPopover icon={Building2} label={t('org.switcher')}>
        <p className="mb-2 text-xs font-medium text-[var(--fv-text-muted)]">{t('org.switcher')}</p>
        <Select
          className="block w-full"
          value={org?.id ?? ''}
          onValueChange={(id) => void switchOrg(id)}
          options={options}
          aria-label={t('org.switcher')}
        />
      </SidebarCompactPopover>
    );
  }

  if (variant === 'sidebar') {
    return (
      <div className="border-b border-white/10 px-4 py-2">
        <p className="text-xs font-medium text-[var(--fv-sidebar-muted)]">{t('org.switcher')}</p>
        <Select
          className="mt-1.5 block w-full"
          tone="sidebar"
          value={org?.id ?? ''}
          onValueChange={(id) => void switchOrg(id)}
          options={options}
          aria-label={t('org.switcher')}
        />
      </div>
    );
  }

  return (
    <label className="flex items-center gap-2 text-sm">
      <span className="text-[var(--fv-text-muted)]">{t('org.switcher')}</span>
      <Select
        className="min-w-[12rem]"
        value={org?.id ?? ''}
        onValueChange={(id) => void switchOrg(id)}
        options={options}
        aria-label={t('org.switcher')}
      />
    </label>
  );
}
